import React, { useEffect, useRef, useState } from 'react';
import TableDataLayer from "../components/TableDataLayer";
import { Icon } from '@iconify/react/dist/iconify.js';
import { Link } from 'react-router-dom';
import { apiCall } from "../utils/apiService";

const MessageLogLayer = () => {
    const [data, setData] = useState([]);
    const [sentCount, setSentCount] = useState(0);
    const [queueCount, setQueueCount] = useState(0);
    const [invalidCount, setInvalidCount] = useState(0);
    const [isSpinning, setIsSpinning] = useState(false);
    const [currentFilter, setCurrentFilter] = useState('');
    const fetchMessageLogs = async (page = 1, limit = 10, search, filter = currentFilter) => {
        try {
            setIsSpinning(true);

            // Build query string dynamically
            const queryParams = new URLSearchParams();
            if (page) queryParams.append('page', page);
            if (limit) queryParams.append('limit', limit);
            if (search) queryParams.append('search', search);
            if (filter) queryParams.append('status', filter);

            const response = await apiCall({
                url: `user/get-messages-log?${queryParams.toString()}`,
                method: 'POST',
                body: { type: 'single' }
            });
            await new Promise((resolve) => setTimeout(resolve, 2000));
            setData(response.total);
            setSentCount(response.sent_count);
            setQueueCount(response.queue_count);
            setInvalidCount(response.invalid_count);
            return response;
        } catch (error) {
            console.error('Error fetching dashboard:', error);
        } finally {
            setIsSpinning(false);
        }
    };

    const removeMessageLog = async (selected, type = null) => {
        try {
            const response = await apiCall({
                url: `user/del-logs`,
                method: 'POST',
                body: { selected: selected, type: type }
            });
            if (response.success) {
                setData(data - 1);
            }
        } catch (error) {
            console.error('Error fetching dashboard:', error);
        }
    };

    const columns = [
        { key: 'instance_name', label: 'Instance' },
        { key: 'mobile', label: 'Mobile' },
        {
            key: 'content',
            label: 'Text',
            render: (val) => {
                const data = val ? JSON.parse(val) : '';
                return data.text;
            },
        },
        {
            key: 'status',
            label: 'Status',
            render: (val) => (
                <span className={`badge bg-${val == 1 ? 'success-main' : 'danger'}`}>
                    {val == 1 ? 'Sent' : 'Not Sent'}
                </span>
            ),
        },
        { key: 'reason', label: 'Reason' },
        {
            key: 'received_datetime',
            label: 'Received',
            render: (val) => {
                const date = new Date(val);
                return date.toLocaleString();
            },
        },
        {
            key: 'createdAt',
            label: 'Sended',
            render: (val) => {
                const date = new Date(val);
                return date.toLocaleString();
            },
        },
        {
            key: 'actions',
            label: 'Actions',
            render: (val, row) => (
                <>
                    <button
                        onClick={() => removeMessageLog([row.id])}
                        className="w-32-px h-32-px me-8 bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                    >
                        <Icon icon="mingcute:delete-2-line" />
                    </button>
                </>

            ),
        },
    ];

    const wsRef = useRef(null);
    const reconnectTimerRef = useRef(null);

    useEffect(() => {
        document.title = 'Message Log List';
    }, []);

    useEffect(() => {
        const token = localStorage.getItem('token');
        const apiBase = process.env.REACT_APP_API_URL;
        if (!token || !apiBase) return undefined;

        const buildWsUrl = () => {
            try {
                const u = new URL(apiBase);
                const wsProto = u.protocol === 'https:' ? 'wss:' : 'ws:';
                const basePath = (u.pathname || '').replace(/\/+$/, '') || '';
                const path = `${basePath}/ws/message-log-stats`;
                return `${wsProto}//${u.host}${path}?token=${encodeURIComponent(token)}`;
            } catch {
                return null;
            }
        };

        let stopped = false;

        const clearReconnect = () => {
            if (reconnectTimerRef.current) {
                clearTimeout(reconnectTimerRef.current);
                reconnectTimerRef.current = null;
            }
        };

        const connect = () => {
            if (stopped) return;
            const url = buildWsUrl();
            if (!url) return;

            clearReconnect();
            try {
                const ws = new WebSocket(url);
                wsRef.current = ws;

                ws.onmessage = (ev) => {
                    try {
                        const msg = JSON.parse(ev.data);
                        if (msg.type === 'message_log_stats') {
                            setSentCount(Number(msg.sent_count) || 0);
                            setInvalidCount(Number(msg.invalid_count) || 0);
                            setQueueCount(Number(msg.queue_count) || 0);
                        }
                    } catch {
                        /* ignore malformed frames */
                    }
                };

                ws.onclose = () => {
                    wsRef.current = null;
                    if (!stopped) {
                        reconnectTimerRef.current = setTimeout(connect, 4000);
                    }
                };
            } catch {
                if (!stopped) {
                    reconnectTimerRef.current = setTimeout(connect, 4000);
                }
            }
        };

        connect();

        return () => {
            stopped = true;
            clearReconnect();
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
        };
    }, []);

    return (
        <>
            <div className="row mb-3">
                <div className="col">
                    <div className="card shadow-none border bg-gradient-start-1" style={{ fontSize: '13px' }}>
                        <div className="card-body p-12">
                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                                <div>
                                    <p className="fw-medium text-success-light mb-1">Total Sent</p>
                                    <p className="mb-0" style={{ fontSize: '18px' }}><b>{sentCount}</b></p>
                                    <button onClick={() => removeMessageLog(null, 'sent')} className='badge btn-danger px-3'>Clear</button>
                                    <button onClick={() => {
                                        setCurrentFilter('1');
                                        fetchMessageLogs(1, 10, '', '1');
                                    }} className='badge btn-success ms-2 px-3'>View</button>
                                </div>
                                <div className="w-50-px h-50-px bg-cyan rounded-circle d-flex justify-content-center align-items-center">
                                    <Icon
                                        icon="gridicons:multiple-users"
                                        className="text-white text-2xl mb-0"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col">
                    <div className="card shadow-none border bg-gradient-start-5" style={{ fontSize: '13px' }}>
                        <div className="card-body p-12">
                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                                <div>
                                    <p className="fw-medium text-success-light mb-1">Total Invalid</p>
                                    <p className="mb-0" style={{ fontSize: '18px' }}><b>{invalidCount}</b></p>
                                    <button onClick={() => removeMessageLog(null, 'invalid')} className='badge btn-danger px-3'>Clear</button>
                                    <button onClick={() => {
                                        setCurrentFilter('0');
                                        fetchMessageLogs(1, 10, '', '0');
                                    }} className='badge btn-success ms-2 px-3'>View</button>
                                </div>
                                <div className="w-50-px h-50-px bg-red rounded-circle d-flex justify-content-center align-items-center">
                                    <Icon
                                        icon="fa6-solid:file-invoice-dollar"
                                        className="text-white text-2xl mb-0"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* card end */}
                </div>
                <div className="col">
                    <div className="card shadow-none border bg-gradient-start-4" style={{ fontSize: '13px' }}>
                        <div className="card-body p-12">
                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                                <div>
                                    <p className="fw-medium text-success-light mb-1">Total In Queue</p>
                                    <p className="mb-0" style={{ fontSize: '18px' }}><b>{queueCount}</b></p>
                                    <button onClick={() => removeMessageLog(null, 'queue')} className='badge btn-danger px-3'>Clear</button>
                                </div>
                                <div className="w-50-px h-50-px bg-success-main rounded-circle d-flex justify-content-center align-items-center">
                                    <Icon
                                        icon="solar:wallet-bold"
                                        className="text-white text-2xl mb-0"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* card end */}
                </div>
            </div>
            {/* TableDataLayer */}
            <div className="card basic-data-table">
                <div className="card-header">
                    <div className="d-flex justify-content-between alig-items-center">
                        <h5 className="card-title mb-0">Message Log List</h5>
                        <Icon onClick={() => {
                            setCurrentFilter('');
                            fetchMessageLogs(1, 10, '', '');
                        }} style={{
                            cursor: 'pointer',
                            transition: 'transform 1s linear',
                            transform: isSpinning ? 'rotate(360deg)' : 'rotate(0deg)',
                        }} icon="material-symbols:refresh" width={24} height={24} />
                    </div>
                </div>
                <TableDataLayer
                    columns={columns}
                    totalRows={data}
                    fetchData={fetchMessageLogs}
                />
            </div>
        </>
    );
}

export default MessageLogLayer